import { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useSelector } from 'react-redux';

import { RootState } from '../../../../store';
import { selectIsLogged } from '../../../../store/auth/authReducer';
import AnimatedLoader from '../../common/AnimatedLoader';
import { Pressable } from '../../common/Touchable';
import LoginWelcome from './LoginWelcome';

type ILoginPlayer = {
	username: string;
	password: string;
};

type LoginErrors = {
	username?: string;
	password?: string;
};

interface LoginModalProps {
	onClose: () => void;
	data: ILoginPlayer;
	errors: LoginErrors;
	onChange: (name: keyof ILoginPlayer, value: string) => void;
	setErrors: (errors: LoginErrors) => void;
	showCloseIcon?: boolean;
}

/** The log in screen: the redesign's whole-screen welcome, closed as soon as the player is in. */
const LoginModal = ({
	onClose,
	data,
	errors,
	onChange,
	setErrors,
	showCloseIcon = true,
}: LoginModalProps) => {
	const isLogged = useSelector(selectIsLogged);
	const authLoading = useSelector((state: RootState) => state.auth.loading);
	const showLoadingOverlay = Boolean(authLoading);

	useEffect(() => {
		if (isLogged) onClose();
	}, [isLogged, onClose]);

	return (
		// The welcome screen's scroll view keeps the focused field above the keyboard itself.
		<View style={styles.root}>
			<LoginWelcome
				data={data}
				errors={errors}
				onChange={onChange}
				setErrors={setErrors}
				onClose={showCloseIcon ? onClose : undefined}
				disabled={showLoadingOverlay}
			/>
			{showLoadingOverlay ? (
				<Pressable style={styles.loadingOverlay} onPress={() => undefined}>
					<View>
						<AnimatedLoader scale={0.6} />
					</View>
				</Pressable>
			) : null}
		</View>
	);
};

const styles = StyleSheet.create({
	root: { flex: 1 },
	loadingOverlay: {
		...StyleSheet.absoluteFillObject,
		backgroundColor: 'rgba(0,0,0,0.45)',
		alignItems: 'center',
		justifyContent: 'center',
	},
});

export default LoginModal;
